'use client';

import { useState } from 'react';
import PropTypes from 'prop-types';
import { Linkedin, Twitter, Link2, Check } from 'lucide-react';

const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : null) ||
  'https://identio.fi';

const COPY = {
  en: { share: 'Share', tweet: 'Tweet', copyLink: 'Copy link', copied: 'Copied', ariaCopy: 'Copy link', ariaCopied: 'Link copied', ariaLinkedIn: 'Share on LinkedIn', ariaTwitter: 'Share on X (Twitter)' },
  fi: { share: 'Jaa', tweet: 'Twiittaa', copyLink: 'Kopioi linkki', copied: 'Kopioitu', ariaCopy: 'Kopioi linkki', ariaCopied: 'Linkki kopioitu', ariaLinkedIn: 'Jaa LinkedInissä', ariaTwitter: 'Jaa X (Twitter)' },
};

export function ShareButtons({ slug, basePath = '/blog', lang = 'en', className = '' }) {
  const [copied, setCopied] = useState(false);
  const base = basePath.replace(/\/$/, '');
  const url = `${SITE_URL.replace(/\/$/, '')}${base}/${slug}`;
  const t = COPY[lang] ?? COPY.en;

  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const btn = 'px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm inline-flex items-center gap-2 text-gray-700';

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      <a
        href={shareLinks.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        className={btn}
        aria-label={t.ariaLinkedIn}
      >
        <Linkedin className="w-4 h-4 shrink-0" />
        {t.share}
      </a>
      <a
        href={shareLinks.twitter}
        target="_blank"
        rel="noopener noreferrer"
        className={btn}
        aria-label={t.ariaTwitter}
      >
        <Twitter className="w-4 h-4 shrink-0" />
        {t.tweet}
      </a>
      <button
        type="button"
        onClick={copyLink}
        className={btn}
        aria-label={copied ? t.ariaCopied : t.ariaCopy}
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 shrink-0 text-green-600" />
            {t.copied}
          </>
        ) : (
          <>
            <Link2 className="w-4 h-4 shrink-0" />
            {t.copyLink}
          </>
        )}
      </button>
    </div>
  );
}

ShareButtons.propTypes = {
  slug: PropTypes.string.isRequired,
  basePath: PropTypes.string,
  lang: PropTypes.oneOf(['en', 'fi']),
  className: PropTypes.string,
};
